import { useMergedEventHandlers } from "@fhs/utils";
import { createContext, forwardRef, useContext, useMemo, useState } from "react";
import { Platform, Pressable as RnPressable, StyleSheet, View } from "react-native";
import type { PressableProps as RnPressableProps, StyleProp, ViewStyle } from "react-native";

export type PressableState = {
  pressed: boolean;
  hovered: boolean;
  focused: boolean;
  disabled: boolean;
};

const PressableContext = createContext<PressableState>({
  pressed: false,
  hovered: false,
  focused: false,
  disabled: false,
});

export function usePressableState() {
  return useContext(PressableContext);
}

export type PressableProps = Omit<RnPressableProps, "style"> & {
  style?: StyleProp<ViewStyle>;
  pressedStyle?: StyleProp<ViewStyle>;
  hoveredStyle?: StyleProp<ViewStyle>;
  focusedStyle?: StyleProp<ViewStyle>;
  disabledStyle?: StyleProp<ViewStyle>;
};

export const Pressable = forwardRef<View, PressableProps>(function Pressable(
  { children, ...props }: PressableProps,
  forwardedRef,
) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const disabled = props.disabled ?? false;

  const pressableState: PressableState = useMemo(
    () => ({ pressed, hovered, focused, disabled }),
    [pressed, hovered, focused, disabled],
  );

  const handleOnPressIn = useMergedEventHandlers(props.onPressIn, () => setPressed(true));
  const handleOnPressOut = useMergedEventHandlers(props.onPressOut, () => setPressed(false));

  const handleOnHoverIn = useMergedEventHandlers(props.onHoverIn, () => setHovered(true));
  const handleOnHoverOut = useMergedEventHandlers(props.onHoverOut, () => setHovered(false));

  const handleOnFocus = useMergedEventHandlers(props.onFocus, () => setFocused(true));
  const handleOnBlur = useMergedEventHandlers(props.onBlur, () => setFocused(false));

  return (
    <PressableContext.Provider value={pressableState}>
      <RnPressable
        ref={forwardedRef}
        {...props}
        disabled={disabled}
        onPressIn={handleOnPressIn}
        onPressOut={handleOnPressOut}
        onHoverIn={handleOnHoverIn}
        onHoverOut={handleOnHoverOut}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        style={[
          styles.baseline,
          props.style,
          pressed && props.pressedStyle,
          focused && props.focusedStyle,
          hovered && props.hoveredStyle,
          disabled && [styles.disabled, props.disabledStyle],
        ]}
      >
        {children}
      </RnPressable>
    </PressableContext.Provider>
  );
});

const styles = StyleSheet.create({
  // @ts-expect-error cursor is valid for web only
  baseline: {
    ...Platform.select({ web: { cursor: "auto" } }),
  },

  // @ts-expect-error cursor is valid for web only
  disabled: {
    ...Platform.select({ web: { cursor: "not-allowed" } }),
  },
});
